/**
 * A store abstraction that falls back to in-memory storage
 * when localStorage is unavailable (e.g., in incognito mode, SSR, or when blocked by browser settings).
 */
export class LocalStorageStore {
	#inMemoryStorage: Record<string, string> = {};
	#inMemoryStorageUsed: boolean = false;

	/**
	 * Sets a value in store.
	 * @param {string} key - The key to store the value under.
	 * @param {string} value - The value to store.
	 * @returns {void}
	 */
	set(key: string, value: string): void {
		try {
			localStorage.setItem(key, value);
		} catch {
			this.#inMemoryStorage[key] = value;
			this.#inMemoryStorageUsed = true;
		}
	}

	/**
	 * Gets a value from store.
	 * @param {string} key - The key to retrieve the value for.
	 * @returns {string | null} The stored value, or null if not found.
	 */
	get(key: string): string | null {
		if (this.#inMemoryStorageUsed) {
			return this.#inMemoryStorage.hasOwnProperty(key) ? this.#inMemoryStorage[key] : null;
		}

		try {
			const value = localStorage.getItem(key);
			return value ?? null;
		} catch {
			this.#inMemoryStorageUsed = true;
			return this.#inMemoryStorage.hasOwnProperty(key) ? this.#inMemoryStorage[key] : null;
		}
	}

	/*
	 * Removes a value from store.
	 * @param {string} key - The key to remove the value for.
	 * @returns {void}
	 */
	remove(key: string): void {
		delete this.#inMemoryStorage[key];
		try {
			localStorage.removeItem(key);
		} catch {
			this.#inMemoryStorageUsed = true;
		}
	}
}
